<template>
  <div
    class="performance-card"
    data-cy="performance-card"
  >
    <!-- On the text baseline, so the chosen word's underline sits below the line like an underline -->
    <div class="d-flex flex-wrap align-items-baseline performance-card__header">
      <h6 class="performance-card__title">
        Performance by
      </h6>

      <button
        v-for="option in DIMENSIONS"
        :key="option.key"
        type="button"
        class="btn btn-sm performance-card__dimension"
        :class="{ 'performance-card__dimension--active': option.key === dimension }"
        :aria-pressed="option.key === dimension ? 'true' : 'false'"
        data-cy="performance-dimension"
        @click="dimension = option.key"
      >
        {{ option.label }}
      </button>

      <!-- On every view with rows, so it does not come and go as the buyer switches between them -->
      <button
        v-if="rows.length > 0"
        type="button"
        class="btn btn-link btn-sm p-0 ml-auto text-muted"
        data-cy="performance-view-all"
        @click="$bvModal.show(modalId)"
      >
        View all
      </button>
    </div>

    <div
      v-if="rows.length === 0"
      class="performance-card__empty"
      data-cy="performance-empty"
    >
      No calls with a {{ current.label.toLowerCase() }} in the selected period
    </div>

    <!-- Busiest first, down the first column, then down the second -->
    <div
      v-else
      class="d-flex performance-card__columns"
    >
      <b-table
        v-for="(columnRows, index) in previewColumns"
        :key="index"
        :fields="fields"
        :items="columnRows"
        class="performance-card__table mb-0"
        small
        borderless
        data-cy="performance-table"
      >
        <!-- Its place among the busiest, then channels as their coloured badges; traffic sources and campaigns as
             plain text -->
        <template #cell(name)="row">
          <span
            class="performance-card__rank"
            data-cy="performance-rank"
          >{{ rank(index, row.index) }}</span>
          <ChannelPill
            v-if="dimension === 'channel'"
            class="performance-card__name"
            :channel="row.item.name"
            :color="channelColors[row.item.id]"
            data-cy="performance-name"
            @click="select(row.item)"
          />
          <button
            v-else
            type="button"
            class="performance-card__name performance-card__name--text"
            :title="`Show only calls from ${row.item.name}`"
            data-cy="performance-name"
            @click="select(row.item)"
          >
            {{ row.item.name }}
          </button>
        </template>

        <template #head(costPerSale)="head">
          <span
            title="CPA, cost per acquisition - the cost of this row's calls divided by its sales. Calls without a sale count toward the cost; - when there is no sale"
            class="performance-card__help"
            data-cy="performance-cpa-head"
          >{{ head.label }}</span>
        </template>

        <template #head(costPerCall)="head">
          <span
            title="RPC, cost per call - the cost of this row's calls divided by the number of calls"
            class="performance-card__help"
            data-cy="performance-rpc-head"
          >{{ head.label }}</span>
        </template>

        <template #head(averageDurationInSeconds)="head">
          <span
            title="ACL, average call length - the average duration of the calls in this row"
            class="performance-card__help"
            data-cy="performance-acl-head"
          >{{ head.label }}</span>
        </template>

        <template #cell(costPerSale)="row">
          {{ formatCostPerSale(row.item) }}
        </template>

        <template #cell(costPerCall)="row">
          {{ formatCostPerCall(row.item) }}
        </template>

        <template #cell(averageDurationInSeconds)="row">
          {{ secondsToDuration(row.item.averageDurationInSeconds) }}
        </template>
      </b-table>
    </div>

    <b-modal
      :id="modalId"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      :title="`Performance by ${current.label}`"
      size="lg"
      ok-only
      ok-title="Close"
      button-size="sm"
    >
      <b-table
        :fields="sortableFields"
        :items="rows"
        thead-class="text-nowrap"
        tbody-tr-class="text-nowrap"
        responsive
        sort-by="callCount"
        sort-desc
        data-cy="performance-all"
      >
        <template #cell(name)="row">
          <ChannelPill
            v-if="dimension === 'channel'"
            class="performance-card__name"
            :channel="row.item.name"
            :color="channelColors[row.item.id]"
            @click="selectFromModal(row.item)"
          />
          <button
            v-else
            type="button"
            class="performance-card__name performance-card__name--text"
            :title="`Show only calls from ${row.item.name}`"
            @click="selectFromModal(row.item)"
          >
            {{ row.item.name }}
          </button>
        </template>

        <template #head(costPerSale)="head">
          <span
            title="CPA, cost per acquisition - the cost of this row's calls divided by its sales. Calls without a sale count toward the cost; - when there is no sale"
            class="performance-card__help"
            data-cy="performance-cpa-head"
          >{{ head.label }}</span>
        </template>

        <template #head(costPerCall)="head">
          <span
            title="RPC, cost per call - the cost of this row's calls divided by the number of calls"
            class="performance-card__help"
            data-cy="performance-rpc-head"
          >{{ head.label }}</span>
        </template>

        <template #head(averageDurationInSeconds)="head">
          <span
            title="ACL, average call length - the average duration of the calls in this row"
            class="performance-card__help"
            data-cy="performance-acl-head"
          >{{ head.label }}</span>
        </template>

        <template #cell(costPerSale)="row">
          {{ formatCostPerSale(row.item) }}
        </template>

        <template #cell(costPerCall)="row">
          {{ formatCostPerCall(row.item) }}
        </template>

        <template #cell(averageDurationInSeconds)="row">
          {{ secondsToDuration(row.item.averageDurationInSeconds) }}
        </template>
      </b-table>
    </b-modal>
  </div>
</template>

<script>
import { BTable } from 'bootstrap-vue'
import ChannelPill from '@/components/buyer/ChannelPill.vue'
import dateFormatMixin from '@/mixins/dateFormatMixin'
import formatCurrency from '@/utils/formatCurrency'

// Two columns of three
const ROWS_PER_COLUMN = 3

const PREVIEW_ROWS = ROWS_PER_COLUMN * 2

// `filterField` is the call logs filter a click on a name sets
const DIMENSIONS = Object.freeze([
  { key: 'channel', label: 'Channel', filterField: 'channels' },
  { key: 'trafficSource', label: 'Traffic Source', filterField: 'trafficSourceIds' },
  { key: 'campaign', label: 'Campaign', filterField: 'campaignIds' }
])

const NUMBER_CELL = { class: 'text-right', thClass: 'text-right' }

const METRIC_FIELDS = [
  { key: 'callCount', label: 'Calls', ...NUMBER_CELL },
  { key: 'saleCount', label: 'Sales', ...NUMBER_CELL },
  { key: 'costPerSale', label: 'CPA', ...NUMBER_CELL },
  { key: 'costPerCall', label: 'RPC', ...NUMBER_CELL },
  { key: 'averageDurationInSeconds', label: 'ACL', ...NUMBER_CELL }
]

export default {
  name: 'PerformanceCard',

  components: {
    BTable,
    ChannelPill
  },

  mixins: [dateFormatMixin],

  props: {
    // Each busiest first: channels as [{ channel, ... }], the others as [{ id, name, ... }], all with
    // callCount, saleCount, totalCost and averageDurationInSeconds
    channels: {
      type: Array,
      default: () => []
    },

    trafficSources: {
      type: Array,
      default: () => []
    },

    campaigns: {
      type: Array,
      default: () => []
    },

    // Colour by channel name, the same ones the call logs table uses
    channelColors: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      DIMENSIONS,
      dimension: 'channel',
      modalId: 'performance-modal'
    }
  },

  computed: {
    current() {
      return DIMENSIONS.find(option => option.key === this.dimension)
    },

    // One shape for all three: { id, name, callCount, ... }
    rows() {
      const source = {
        channel: this.channels.map(row => ({ ...row, id: row.channel, name: row.channel })),
        trafficSource: this.trafficSources,
        campaign: this.campaigns
      }[this.dimension]

      return source.filter(row => row.callCount > 0)
    },

    previewColumns() {
      const preview = this.rows.slice(0, PREVIEW_ROWS)

      return [preview.slice(0, ROWS_PER_COLUMN), preview.slice(ROWS_PER_COLUMN)].filter(column => column.length > 0)
    },

    fields() {
      return [{ key: 'name', label: this.current.label }, ...METRIC_FIELDS]
    },

    sortableFields() {
      return this.fields.map(field => ({
        ...field,
        sortable: true,
        // The derived columns sort by their value, not by the empty item key
        ...(field.key === 'costPerSale' ? { sortByFormatted: true, formatter: (value, key, item) => this.costPerSale(item) } : {}),
        ...(field.key === 'costPerCall' ? { sortByFormatted: true, formatter: (value, key, item) => this.costPerCall(item) } : {})
      }))
    }
  },

  methods: {
    // 1 to 6, down the first column, then down the second
    rank(columnIndex, rowIndex) {
      return columnIndex * ROWS_PER_COLUMN + rowIndex + 1
    },

    // No sale means no cost per sale, rather than an infinite one; sorts last
    costPerSale(row) {
      return row.saleCount > 0 ? row.totalCost / row.saleCount : -1
    },

    costPerCall(row) {
      return row.callCount > 0 ? row.totalCost / row.callCount : 0
    },

    formatCostPerSale(row) {
      return row.saleCount > 0 ? formatCurrency(this.costPerSale(row)) : '-'
    },

    formatCostPerCall(row) {
      return formatCurrency(this.costPerCall(row))
    },

    // The page filters its calls to this channel, traffic source or campaign
    select(row) {
      this.$emit('select', { field: this.current.filterField, id: row.id })
    },

    selectFromModal(row) {
      this.$bvModal.hide(this.modalId)
      this.select(row)
    }
  }
}
</script>

<style lang="scss" scoped>
// Room on the right, so the last column and View all keep clear of the Disputes card beside it
.performance-card {
  padding-right: 0.75rem;
}

.performance-card__header {
  gap: 0.625rem;
  margin-bottom: 0.5rem;
}

.performance-card__title {
  font-weight: bold;
  margin: 0 0.25rem 0 0;
}

// Plain words after the title: the chosen one white and underlined, the others dimmed
.performance-card__dimension {
  padding: 0 0.125rem;
  border: 0;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  background: none;
  color: $secondary;
  font-size: 1rem;
  line-height: 1.3;

  &:hover,
  &:focus {
    color: var(--white);
    box-shadow: none;
  }

  &--active {
    border-bottom-color: #5b8def;
    color: var(--white);
  }
}

// Small and grey, so it reads as an order rather than data
.performance-card__rank {
  display: inline-block;
  min-width: 0.75rem;
  margin-right: 0.375rem;
  color: $secondary;
  font-size: 0.7rem;
  // Centred on the name beside it, which sits in the middle of the row
  vertical-align: middle;
}

.performance-card__help {
  cursor: help;
}

.performance-card__empty {
  font-size: 0.8rem;
  opacity: 0.7;
}

// Top-aligned: a shorter second column keeps its row height instead of stretching to the first. When the card is
// narrower than the two tables, it scrolls sideways instead of letting the rows spill out
.performance-card__columns {
  align-items: flex-start;
  overflow-x: auto;
  // Thin and dim, not the browser's white bar, on the dark theme
  scrollbar-color: rgba(138, 148, 166, 0.5) transparent;
  scrollbar-width: thin;
}

// A thin grey line between the two columns, on the first: it always has the most rows, so the line runs full height.
// Tables take no padding, so the cells either side of the line get the same room instead
.performance-card__table:not(:last-child) {
  border-right: 1px solid rgba(138, 148, 166, 0.3);

  ::v-deep th:last-child,
  ::v-deep td:last-child {
    padding-right: 1rem;
  }
}

.performance-card__table + .performance-card__table {
  ::v-deep th:first-child,
  ::v-deep td:first-child {
    padding-left: 1rem;
  }
}

.performance-card__table {
  // Equal shares of the card, shrinking down to the narrowest the rows go without wrapping, never below: only then
  // does the card scroll. Bootstrap's 100% width would make each table claim the whole card
  flex: 1 1 0;
  min-width: min-content;
  width: auto;
  font-size: 0.8rem;
}

// Through the columns wrapper, so it outranks the Call Logs page's own table cell padding, which also reaches here
.performance-card__columns .performance-card__table {
  ::v-deep th,
  ::v-deep td {
    padding: 0.2rem 0.3rem;
    vertical-align: middle;
    white-space: nowrap;
  }
}

.performance-card__name {
  max-width: 14rem;
  overflow: hidden;
  padding: 0 0.375rem;
  font-size: 0.75rem;
  text-overflow: ellipsis;
}

// Plain text in white, standing out from the figures, still a button: a click filters the call logs to it
.performance-card__name--text {
  display: inline-block;
  padding: 0;
  border: 0;
  background: none;
  color: var(--white);
  font-size: inherit;
  white-space: nowrap;
  vertical-align: middle;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
}
</style>
